import { debounce } from '@repo/utils/debounce'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { storeWrapper } from '../storeWrapper'

const initialState = {
  isUndertakingsSupported: false,
  undertaking: null,
  undertakings: [],
  undertakingsCounts: []
}

export const useUndertakingsStore = storeWrapper('useUndertakingsStore', (set, get) => ({
  ...initialState,
  fetchIsUndertakingsSupported: async () => {
    return new Promise((resolve) => {
      debounce({
        id: 'fetchIsUndertakingsSupported',
        delay: 300,
        callback: async () => {
          let isSupported = await ospreyAPI({
            service: 'matters',
            endpoint: '/MatterUndertaking/IsUndertakingsSupported'
          })

          const isUndertakingsSupported = isSupported === true || isSupported === 'true'
          set({ isUndertakingsSupported })
          resolve(isUndertakingsSupported)
        }
      })
    })
  },
  fetchMatterUndertakingCount: async ({ clientNo, matterNo }) => {
    return new Promise((resolve, reject) => {
      const { isUndertakingsSupported } = get()
      if (!isUndertakingsSupported) {
        resolve(null)
        return
      }

      debounce({
        id: `fetchMatterUndertakingCount-${clientNo}-${matterNo}`,
        delay: 300,
        callback: async () => {
          try {
            const undertakingCounts = await ospreyAPI({
              service: 'matters',
              endpoint: `/MatterUndertaking/GetRelatedMatterUndertakingCountByClientNoAndMatterNo`,
              data: { clientNo, matterNo }
            })

            const clientNoString = clientNo != null ? String(clientNo) : ''
            const matterNoNumber = matterNo != null && matterNo !== '' ? Number(matterNo) : NaN

            const totalCount = Number(undertakingCounts?.totalCount ?? 0)
            const dischargedCount = Number(undertakingCounts?.dischargedCount ?? 0)

            const matterHasOutstandingUndertakings = Boolean(totalCount !== dischargedCount)

            const currentUndertakingCount = {
              clientNo: clientNoString,
              matterNo: matterNoNumber,
              ...undertakingCounts,
              matterHasOutstandingUndertakings
            }

            set((state) => {
              const existingIndex = state.undertakingsCounts.findIndex(
                (item) => item.clientNo === clientNoString && item.matterNo === matterNoNumber
              )

              if (existingIndex === -1) {
                return {
                  undertakingsCounts: [...state.undertakingsCounts, currentUndertakingCount]
                }
              }

              const updated = [...state.undertakingsCounts]
              updated[existingIndex] = currentUndertakingCount

              return { undertakingsCounts: updated }
            })

            resolve(undertakingCounts)
          } catch (error) {
            reject(error)
          }
        }
      })
    })
  },
  fetchAllUndertakings: async () => {
    return new Promise((resolve) => {
      const { isUndertakingsSupported } = get()
      if (!isUndertakingsSupported) {
        resolve(null)
        return
      }

      debounce({
        id: `fetchAllUndertakings`,
        delay: 300,
        callback: async () => {
          let undertakings = await ospreyAPI({
            service: 'matters',
            endpoint: `/MatterUndertaking/GetAll`
          })

          set({ undertakings: undertakings })
          resolve(undertakings)
        }
      })
    })
  },
  fetchAllUndertakingsByClientNoAndMatterNo: async ({ clientNo, matterNo }) => {
    return new Promise((resolve) => {
      const { isUndertakingsSupported } = get()
      if (!isUndertakingsSupported) {
        resolve(null)
        return
      }

      debounce({
        id: `fetchAllUndertakingsByClientNoAndMatterNo-${clientNo}-${matterNo}`,
        delay: 300,
        callback: async () => {
          let undertakings = await ospreyAPI({
            service: 'matters',
            endpoint: `/MatterUndertaking/GetAllRelatedByClientNoAndMatterNo`,
            data: { clientNo: clientNo, matterNo: matterNo }
          })

          set({ undertakings: undertakings })
          resolve(undertakings)
        }
      })
    })
  },
  fetchSingleUndertaking: async ({ clientNo, matterNo }) => {
    return new Promise((resolve) => {
      const { isUndertakingsSupported } = get()
      if (!isUndertakingsSupported) {
        resolve(null)
        return
      }

      debounce({
        id: `fetchSingleUndertaking-${clientNo}-${matterNo}`,
        delay: 300,
        callback: async () => {
          let undertaking = await ospreyAPI({
            service: 'matters',
            endpoint: `/MatterUndertaking/Get`,
            data: { clientNo: clientNo, matterNo: matterNo }
          })

          set({ undertaking: undertaking })
          resolve(undertaking)
        }
      })
    })
  },
  fetchUndertakingExists: async ({ clientNo, matterNo }) => {
    return new Promise((resolve) => {
      const { isUndertakingsSupported } = get()
      if (!isUndertakingsSupported) {
        resolve(null)
        return
      }

      debounce({
        id: `fetchUndertakingExists-${clientNo}-${matterNo}`,
        delay: 300,
        callback: async () => {
          let exists = await ospreyAPI({
            service: 'matters',
            endpoint: `/MatterUndertaking/Exists`,
            data: { clientNo: clientNo, matterNo: matterNo }
          })

          console.log('exists', exists)
          resolve(exists)
        }
      })
    })
  },
  reset: () => set(initialState)
}))
